import { t } from 'ttag'
import { useQuery } from 'react-query'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import { useSelector } from 'react-redux'

import { api } from '@h2/api'
import { user as getUser } from '@h2/common/selectors/user'
import { Loader } from '@h2/components/loading/loader'
import { Heading } from '@h2/components/typography/heading'
import { Text } from '@h2/components/typography/text'
import { Page } from '@h2/common/components/page'
import { PB97T, SG40S, PB90T, WHITE } from '@h2/components/colors'
import { CollapseBottomWhitespace } from '@h2/common/components/layout'
import { Chip } from '@h2/components/display/chip'
import { Flex } from '@h2/components/layout/flex'
import major from '@h2/assets/svg/icons/major.svg'
import { ClusterIcon } from '@h2/components/icons'

import { SimilarOccupations } from './components/similarOccupations'
import { SimilarMajors } from './components/similarMajors'

import { NextSteps } from '../../opportunities/components/nextSteps'

const PaddedWrap = styled.div`
  padding: 15px;
`
const Section = styled.div`
  background-color: #fff;
  padding: 15px;
  width: 100%;
  margin: 15px 0 15px 0;
`
const TitleSection = styled(Section)`
  background-color: ${SG40S};
  margin-top: 0;
`
const TitleWrapper = styled.div`
  width: 100%;
`
const PageTitleWrapper = styled(TitleWrapper)`
  margin: -15px;
  min-height: 160px;
`
const StyledTitleImage = styled.div`
  display: flex;
  max-height: 200px;
  background-image: ${({ src }) => `url(${src})`};
  background-size: ${({ shouldContain }) => (shouldContain ? 'contain' : 'cover')};
  background-repeat: no-repeat;
  background-position: center;
  background-color: white;
  padding: 6px;
  width: 30%;
  max-width: 300px;
  min-height: 160px;
  align-items: flex-end;
`
const FlexItem = styled.div`
  flex-grow: 1;
  min-width: calc(33% - 15px);
`
const CalloutText = styled(Text)`
  font-size: 36px;
`
const TitleImage = ({ pic }) => {
  return <StyledTitleImage src={pic} shouldContain={true}></StyledTitleImage>
}
const CipDetail = () => {
  const { cipCode } = useParams()
  const userInfo = useSelector(getUser)
  const { data: cipDetails, isLoading } = useQuery(['major-detail', cipCode], () => {
    return api.fetch(`/major/${cipCode}`, {
      method: 'GET'
    })
  })

  if (isLoading) {
    return <Loader />
  }

  if (!cipDetails) {
    return <Text>{t`Could not locate program ${cipCode}`}</Text>
  }

  const OutputSection = ({ value, title }) => {
    if (!value) {
      return null
    }

    return (
      <Section>
        <TitleWrapper>
          <Heading level="4" fontSize="30px">
            {title}
          </Heading>
          {value}
        </TitleWrapper>
      </Section>
    )
  }
  const similarOccs = (
    <SimilarOccupations
      relatedOnet={cipDetails.occupations}
      occupationMeta={cipDetails.occupationMeta}
    />
  )
  const completionsInArea = cipDetails.completionsInArea || {}
  const jobOpeningsInMajor = cipDetails.jobOpeningsInMajor || {}
  const aboutDetails = (
    <>
      <Text>{cipDetails.description}</Text>
      <Flex gap="15px">
        <FlexItem>
          <Heading align="center" level="4">{t`Completions`}</Heading>
          <CalloutText weight="bold" align="center">
            {completionsInArea.completionsTotal
              ? completionsInArea.completionsTotal.toLocaleString()
              : t`n/a`}
          </CalloutText>
        </FlexItem>
        <FlexItem>
          <Heading align="center" level="4">{t`Institutions Offering`}</Heading>
          <CalloutText weight="bold" align="center">
            {completionsInArea.institutionsOffering &&
              completionsInArea.institutionsOffering.toLocaleString()}
          </CalloutText>
        </FlexItem>
        <FlexItem>
          <Heading align="center" level="4">{t`Annual Job Openings`}</Heading>
          <CalloutText weight="bold" align="center">
            {jobOpeningsInMajor.annualOpenings &&
              jobOpeningsInMajor.annualOpenings.toLocaleString()}
          </CalloutText>
        </FlexItem>
      </Flex>
    </>
  )

  return (
    <>
      <Page bgColor={PB97T}>
        <Page.Layout>
          <Page.Content>
            <TitleSection>
              <PageTitleWrapper>
                <Flex gap="25px">
                  <TitleImage pic={major} />
                  <div style={{ padding: '15px' }}>
                    <Heading level="2" fontSize="30px" color={WHITE}>
                      {cipDetails.title}
                    </Heading>
                    {cipDetails.cluster && (
                      <Chip icon={ClusterIcon} size="large">
                        {cipDetails.cluster?.name}
                      </Chip>
                    )}
                  </div>
                </Flex>
              </PageTitleWrapper>
            </TitleSection>
            <OutputSection title={t`About this Program`} value={aboutDetails} />
            <OutputSection
              title={t`Similar Majors`}
              value={<SimilarMajors profile={cipDetails} />}
            />
            <OutputSection title={t`Related Occupations`} value={similarOccs} />
          </Page.Content>
          <Page.Sidebar bgColor={PB90T}>
            <PaddedWrap>
              <NextSteps
                objectId={cipCode}
                objectType={'major'}
                user={userInfo}
                recommendations={cipDetails.recommendations}
              />
            </PaddedWrap>
          </Page.Sidebar>
        </Page.Layout>
      </Page>
      <CollapseBottomWhitespace />
    </>
  )
}

export { CipDetail }
